import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FiArrowLeft,
  FiArrowRight,
  FiCheck,
  FiLock,
  FiPackage,
} from "react-icons/fi";
import { CartContext } from "./CartContext";
import "../css/checkout.css";
import productData, { formatCurrency, siteConfig } from "../Datas/productData";

const initialForm = {
  contact: "",
  country: "United States",
  firstName: "",
  lastName: "",
  company: "",
  address: "",
  apartment: "",
  city: "",
  region: "",
  postalCode: "",
  shippingMethod: "standard",
  cardNumber: "",
  expiration: "",
  security: "",
  cardName: "",
};

const CheckOut = () => {
  const { cartItems, clearCart, orderNote } = useContext(CartContext);
  const navigate = useNavigate();
  const [formData, setFormData] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [isProcessing, setIsProcessing] = useState(false);

  const populatedItems = useMemo(
    () =>
      cartItems
        .map((item) => ({
          ...item,
          product: productData.find((product) => product.id === item.id),
        }))
        .filter((item) => item.product),
    [cartItems]
  );

  const subtotal = populatedItems.reduce(
    (sum, item) => sum + item.product.price * item.quantity,
    0
  );
  const isDomestic = formData.country === "United States";
  const standardShipping = isDomestic
    ? subtotal >= siteConfig.shipping.freeThreshold
      ? 0
      : siteConfig.shipping.standardRate
    : 18;
  const shipping =
    formData.shippingMethod === "express"
      ? isDomestic
        ? 14
        : 28
      : standardShipping;
  const total = subtotal + shipping;

  useEffect(() => {
    document.title = `Checkout — ${siteConfig.brandName}`;
  }, []);

  const updateField = (event) => {
    const { name, value } = event.target;
    let nextValue = value;

    if (name === "cardNumber") {
      nextValue = value
        .replace(/\D/g, "")
        .slice(0, 16)
        .replace(/(\d{4})(?=\d)/g, "$1 ");
    }

    if (name === "expiration") {
      const digits = value.replace(/\D/g, "").slice(0, 4);
      nextValue =
        digits.length > 2 ? `${digits.slice(0, 2)}/${digits.slice(2)}` : digits;
    }

    if (name === "security") {
      nextValue = value.replace(/\D/g, "").slice(0, 4);
    }

    setFormData((current) => ({ ...current, [name]: nextValue }));
    if (errors[name]) {
      setErrors((current) => ({ ...current, [name]: "" }));
    }
  };

  const validate = () => {
    const nextErrors = {};
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const cardDigits = formData.cardNumber.replace(/\s/g, "");
    const [month, year] = formData.expiration.split("/");
    const now = new Date();
    const currentYear = now.getFullYear() % 100;
    const currentMonth = now.getMonth() + 1;

    if (!emailPattern.test(formData.contact.trim())) {
      nextErrors.contact = "Enter a valid email address.";
    }
    if (!formData.firstName.trim())
      nextErrors.firstName = "Enter a first name.";
    if (!formData.lastName.trim()) nextErrors.lastName = "Enter a last name.";
    if (!formData.address.trim()) nextErrors.address = "Enter an address.";
    if (!formData.city.trim()) nextErrors.city = "Enter a city.";
    if (!formData.region.trim()) nextErrors.region = "Enter a state or region.";
    if (!formData.postalCode.trim()) {
      nextErrors.postalCode = "Enter a ZIP or postal code.";
    }
    if (cardDigits.length !== 16) {
      nextErrors.cardNumber = "Enter a 16-digit card number.";
    }
    if (
      !/^\d{2}\/\d{2}$/.test(formData.expiration) ||
      Number(month) < 1 ||
      Number(month) > 12 ||
      Number(year) < currentYear ||
      (Number(year) === currentYear && Number(month) < currentMonth)
    ) {
      nextErrors.expiration = "Use a valid MM/YY date.";
    }
    if (!/^\d{3,4}$/.test(formData.security)) {
      nextErrors.security = "Enter a 3 or 4 digit security code.";
    }
    if (!formData.cardName.trim()) {
      nextErrors.cardName = "Enter the name shown on the card.";
    }

    return nextErrors;
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const nextErrors = validate();
    setErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      const firstInvalid = document.getElementById(Object.keys(nextErrors)[0]);
      firstInvalid?.focus();
      firstInvalid?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    setIsProcessing(true);
    window.setTimeout(() => {
      clearCart();
      navigate("/paysuccess");
      window.scrollTo({ top: 0, behavior: "smooth" });
    }, 900);
  };

  if (populatedItems.length === 0) {
    return (
      <main className="checkout-empty" id="main-content">
        <FiPackage aria-hidden="true" />
        <p className="eyebrow">Nothing to check out just yet</p>
        <h1>Your cart is empty</h1>
        <p>Add a paper treat or two before heading to checkout.</p>
        <Link className="button" to="/shop">
          Browse the shop <FiArrowRight aria-hidden="true" />
        </Link>
      </main>
    );
  }

  const fieldError = (name) =>
    errors[name] ? (
      <span className="checkout-error">{errors[name]}</span>
    ) : null;

  return (
    <main className="checkout-page" id="main-content">
      <div className="checkout-title">
        <p className="eyebrow">Secure demo checkout</p>
        <h1>Checkout</h1>
        <span className="page-divider" />
      </div>

      <form className="checkout-shell" onSubmit={handleSubmit} noValidate>
        <div className="checkout-form">
          <section
            className="checkout-section"
            aria-labelledby="contact-heading"
          >
            <div className="checkout-section__heading">
              <span>1</span>
              <div>
                <h2 id="contact-heading">Contact</h2>
                <p>We’ll send your order confirmation here.</p>
              </div>
            </div>
            <label className="checkout-field checkout-field--full">
              <span>Email address</span>
              <input
                id="contact"
                name="contact"
                type="email"
                value={formData.contact}
                onChange={updateField}
                autoComplete="email"
                aria-invalid={Boolean(errors.contact)}
              />
              {fieldError("contact")}
            </label>
          </section>

          <section
            className="checkout-section"
            aria-labelledby="delivery-heading"
          >
            <div className="checkout-section__heading">
              <span>2</span>
              <div>
                <h2 id="delivery-heading">Delivery</h2>
                <p>Where should we send your paper goods?</p>
              </div>
            </div>

            <div className="checkout-fields">
              <label className="checkout-field checkout-field--full">
                <span>Country / region</span>
                <select
                  name="country"
                  value={formData.country}
                  onChange={updateField}
                  autoComplete="country-name"
                >
                  <option>United States</option>
                  <option>Canada</option>
                  <option>United Kingdom</option>
                  <option>Australia</option>
                  <option>Other</option>
                </select>
              </label>
              <label className="checkout-field">
                <span>First name</span>
                <input
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={updateField}
                  autoComplete="given-name"
                  aria-invalid={Boolean(errors.firstName)}
                />
                {fieldError("firstName")}
              </label>
              <label className="checkout-field">
                <span>Last name</span>
                <input
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={updateField}
                  autoComplete="family-name"
                  aria-invalid={Boolean(errors.lastName)}
                />
                {fieldError("lastName")}
              </label>
              <label className="checkout-field checkout-field--full">
                <span>Company (optional)</span>
                <input
                  name="company"
                  value={formData.company}
                  onChange={updateField}
                  autoComplete="organization"
                />
              </label>
              <label className="checkout-field checkout-field--full">
                <span>Address</span>
                <input
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={updateField}
                  autoComplete="street-address"
                  aria-invalid={Boolean(errors.address)}
                />
                {fieldError("address")}
              </label>
              <label className="checkout-field checkout-field--full">
                <span>Apartment, suite, etc. (optional)</span>
                <input
                  name="apartment"
                  value={formData.apartment}
                  onChange={updateField}
                  autoComplete="address-line2"
                />
              </label>
              <label className="checkout-field">
                <span>City</span>
                <input
                  id="city"
                  name="city"
                  value={formData.city}
                  onChange={updateField}
                  autoComplete="address-level2"
                  aria-invalid={Boolean(errors.city)}
                />
                {fieldError("city")}
              </label>
              <label className="checkout-field">
                <span>State / region</span>
                <input
                  id="region"
                  name="region"
                  value={formData.region}
                  onChange={updateField}
                  autoComplete="address-level1"
                  aria-invalid={Boolean(errors.region)}
                />
                {fieldError("region")}
              </label>
              <label className="checkout-field checkout-field--half">
                <span>ZIP / postal code</span>
                <input
                  id="postalCode"
                  name="postalCode"
                  value={formData.postalCode}
                  onChange={updateField}
                  autoComplete="postal-code"
                  aria-invalid={Boolean(errors.postalCode)}
                />
                {fieldError("postalCode")}
              </label>
            </div>

            <fieldset className="shipping-methods">
              <legend>Shipping method</legend>
              <label>
                <input
                  type="radio"
                  name="shippingMethod"
                  value="standard"
                  checked={formData.shippingMethod === "standard"}
                  onChange={updateField}
                />
                <span>
                  <strong>
                    {isDomestic
                      ? "Standard shipping"
                      : "International standard"}
                  </strong>
                  <small>{isDomestic ? "3–6" : "7–21"} business days</small>
                </span>
                <em>
                  {standardShipping === 0
                    ? "Free"
                    : formatCurrency(standardShipping)}
                </em>
              </label>
              <label>
                <input
                  type="radio"
                  name="shippingMethod"
                  value="express"
                  checked={formData.shippingMethod === "express"}
                  onChange={updateField}
                />
                <span>
                  <strong>Express shipping</strong>
                  <small>{isDomestic ? "1–2" : "3–7"} business days</small>
                </span>
                <em>{formatCurrency(isDomestic ? 14 : 28)}</em>
              </label>
            </fieldset>
          </section>

          <section
            className="checkout-section"
            aria-labelledby="payment-heading"
          >
            <div className="checkout-section__heading">
              <span>3</span>
              <div>
                <h2 id="payment-heading">Payment</h2>
                <p>This front-end demo does not transmit or store card data.</p>
              </div>
            </div>

            <div className="payment-box">
              <div className="payment-box__head">
                <span>Credit card</span>
                <span>
                  <FiLock aria-hidden="true" /> Encrypted entry
                </span>
              </div>
              <div className="payment-box__body">
                <label className="checkout-field checkout-field--full">
                  <span>Card number</span>
                  <input
                    id="cardNumber"
                    name="cardNumber"
                    value={formData.cardNumber}
                    onChange={updateField}
                    inputMode="numeric"
                    autoComplete="cc-number"
                    placeholder="0000 0000 0000 0000"
                    aria-invalid={Boolean(errors.cardNumber)}
                  />
                  {fieldError("cardNumber")}
                </label>
                <label className="checkout-field">
                  <span>Expiration date</span>
                  <input
                    id="expiration"
                    name="expiration"
                    value={formData.expiration}
                    onChange={updateField}
                    inputMode="numeric"
                    autoComplete="cc-exp"
                    placeholder="MM/YY"
                    aria-invalid={Boolean(errors.expiration)}
                  />
                  {fieldError("expiration")}
                </label>
                <label className="checkout-field">
                  <span>Security code</span>
                  <input
                    id="security"
                    name="security"
                    value={formData.security}
                    onChange={updateField}
                    inputMode="numeric"
                    autoComplete="cc-csc"
                    placeholder="CVV"
                    aria-invalid={Boolean(errors.security)}
                  />
                  {fieldError("security")}
                </label>
                <label className="checkout-field checkout-field--full">
                  <span>Name on card</span>
                  <input
                    id="cardName"
                    name="cardName"
                    value={formData.cardName}
                    onChange={updateField}
                    autoComplete="cc-name"
                    aria-invalid={Boolean(errors.cardName)}
                  />
                  {fieldError("cardName")}
                </label>
              </div>
            </div>
          </section>

          <div className="checkout-submit">
            <Link to="/cartpage">
              <FiArrowLeft aria-hidden="true" /> Return to cart
            </Link>
            <button type="submit" disabled={isProcessing}>
              {isProcessing ? (
                "Processing…"
              ) : (
                <>
                  Complete order <FiArrowRight aria-hidden="true" />
                </>
              )}
            </button>
          </div>

          <p className="checkout-legal">
            By completing this demo order, you agree to our{" "}
            <Link to="/terms">terms</Link> and acknowledge our{" "}
            <Link to="/privacy">privacy policy</Link>.
          </p>
        </div>

        <aside className="checkout-summary" aria-label="Order summary">
          <h2>Order summary</h2>
          <div className="checkout-summary__items">
            {populatedItems.map((item) => (
              <article key={`${item.id}-${item.size}-${item.color}`}>
                <div className="checkout-summary__image">
                  <img src={item.product.images[0]} alt="" />
                  <span>{item.quantity}</span>
                </div>
                <div>
                  <h3>{item.product.name}</h3>
                  {item.size && <p>{item.size}</p>}
                </div>
                <strong>
                  {formatCurrency(item.product.price * item.quantity)}
                </strong>
              </article>
            ))}
          </div>
          <dl>
            <div>
              <dt>Subtotal</dt>
              <dd>{formatCurrency(subtotal)}</dd>
            </div>
            <div>
              <dt>Shipping</dt>
              <dd>{shipping === 0 ? "Free" : formatCurrency(shipping)}</dd>
            </div>
            <div className="checkout-summary__total">
              <dt>Total</dt>
              <dd>{formatCurrency(total)}</dd>
            </div>
          </dl>
          {orderNote && (
            <div className="checkout-summary__note">
              <span>Order note</span>
              <p>{orderNote}</p>
            </div>
          )}
          <p className="checkout-summary__promise">
            <FiCheck aria-hidden="true" />
            Carefully packed by a small paper-loving studio.
          </p>
        </aside>
      </form>
    </main>
  );
};

export default CheckOut;
